import axios, { AxiosInstance } from "axios";
import { getAccessToken, refreshAccessToken } from "@/services/keycloak";
import {
  LoginRequest,
  JwtResponse,
  User,
  Content,
  Actor,
  Cast,
  UserManagement,
  Notification,
  NotificationTemplate,
  DashboardStats,
  ContentStats,
  MediaProcessingStatus,
  ProcessingStatus,
  PaginatedResponse,
  ContentFilters,
  NotificationFilters,
  Season,
  Episode,
} from "@/types";


export interface AccountLimitsResponse {
  profiles: {
    maxAdultProfiles: number;
    maxKidProfiles: number;
    maxDevices: number;
    updatedAt?: string;
    updatedBy?: string;
  } | null;
  profilesProblem?: string;
  sessions: {
    installed: boolean;
    maxDevices: number | null;
    behavior: string | null;
    flowAlias: string | null;
    problem: string | null;
  };
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Intercepteur pour ajouter le token JWT
    this.api.interceptors.request.use(
      async (config) => {
        // Le jeton Keycloak ne vit que cinq minutes. Le lire brut dans
        // localStorage revenait à envoyer un jeton périmé dès que l'écran
        // restait ouvert : c'est ce qui faisait échouer l'assemblage d'un
        // téléversement de plusieurs heures. getAccessToken le renouvelle.
        const token = await getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Empêcher les popups d'authentification HTTP basique
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Intercepteur pour gérer les erreurs de réponse
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const requete = error.config as
          | (typeof error.config & { _rejoue?: boolean })
          | undefined;

        // Un 401 ne signifie pas que la session est perdue : le plus souvent
        // le jeton vient d'expirer. On tente un renouvellement et on rejoue
        // l'appel une fois. L'ancienne version effaçait le jeton d'emblée,
        // ce qui déconnectait l'utilisateur en pleine opération.
        if (error.response?.status === 401 && requete && !requete._rejoue) {
          requete._rejoue = true;
          const renouvele = await refreshAccessToken();
          if (renouvele) {
            requete.headers = requete.headers ?? {};
            requete.headers.Authorization = `Bearer ${renouvele.access_token}`;
            return this.api.request(requete);
          }
          // Renouvellement impossible : la session est réellement terminée.
          localStorage.removeItem("accessToken");
          localStorage.removeItem("user");
          // Refine va automatiquement rediriger vers /login via authProvider.check()
        }
        // Le serveur explique pourquoi il refuse — « La classification par âge
        // est obligatoire pour publier », par exemple. Les pages affichent
        // `error.message`, qui sans cela vaut « Request failed with status
        // code 400 » et n'apprend rien à l'utilisateur.
        const explication = (error.response?.data as { message?: string } | undefined)
          ?.message;
        if (explication && typeof explication === "string") {
          error.message = explication;
        }

        return Promise.reject(error);
      }
    );
  }

  // === AUTHENTICATION ===
  async login(credentials: LoginRequest): Promise<JwtResponse> {
    
    try {
      const response = await this.api.post("/auth/signin", credentials);
      
      const jwtResponse = response.data;

      // Stocker le token
      localStorage.setItem("accessToken", jwtResponse.token);
      localStorage.setItem(
        "user",
        JSON.stringify({
          id: jwtResponse.id,
          username: jwtResponse.username,
          email: jwtResponse.email,
          roles: jwtResponse.roles,
        })
      );

      console.log("💾 Token stocké dans localStorage");
      return jwtResponse;
    } catch (error: any) {
      console.error("❌ Erreur de connexion:", error);
      console.error("📊 Détails de l'erreur:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  }

  async logout(): Promise<void> {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("user");
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get("/auth/userinfo");
    return response.data;
  }

  // === DASHBOARD STATS ===
  async getDashboardStats(): Promise<DashboardStats> {
    // Ces chiffres etaient ecrits en dur a 0 : abonnes actifs, vues et chiffre
    // d'affaires n'ont jamais rien affiche de reel. Ils viennent desormais des
    // services concernes. Chaque appel est tolerant a la panne : un service
    // indisponible laisse son indicateur a 0 plutot que de vider tout l'ecran.
    const safe = async <T>(call: Promise<T>, fallback: T): Promise<T> => {
      try {
        return await call;
      } catch {
        return fallback;
      }
    };

    const [allContents, published, drafts, pending, users, viewStats, subscriptions, payments] =
      await Promise.all([
        safe(this.getContents(undefined, 1, 1), { total: 0 } as any),
        safe(this.getContents({ status: "PUBLISHED" }, 1, 1), { total: 0 } as any),
        safe(this.getContents({ status: "DRAFT" }, 1, 1), { total: 0 } as any),
        safe(this.getContents({ status: "PENDING" }, 1, 1), { total: 0 } as any),
        safe(this.getUserStats(), {
          total: 0,
          newLast30Days: 0,
          growthPercent: null,
        } as any),
        safe(this.getViewStatsSummary(), { totalViews: 0, uniqueViewers: 0 }),
        safe(this.getAdminSubscriptions(1, 1, "ACTIVE"), { total: 0 } as any),
        safe(this.getPaymentStatistics(), { totalAmount: 0 } as any),
      ]);

    const abonnesActifs = Number(subscriptions?.total ?? 0);

    return {
      totalUsers: users.total || 0,
      // Un utilisateur "actif" est un abonne en cours de validite
      activeUsers: abonnesActifs,
      // Inscriptions des 30 derniers jours, comptees dans Keycloak
      newUsers: Number(users?.newLast30Days ?? 0),
      premiumUsers: abonnesActifs,
      totalContents: allContents.total || 0,
      publishedContents: published.total || 0,
      draftContents: drafts.total || 0,
      pendingContents: pending.total || 0,
      // Spectateurs distincts. `totalViews` compte les evenements PLAY, donc
      // les relances de lecture : ce n'est pas une audience.
      totalViews: Number(viewStats?.uniqueViewers ?? 0),
      totalRevenue: Number(payments?.totalAmount ?? 0),
      monthlyGrowth: {
        // Seule la croissance des inscriptions est mesurable aujourd'hui ;
        // les autres restent a 0 plutot que d'etre inventees.
        users: Number(users?.growthPercent ?? 0),
        contents: 0,
        views: 0,
        revenue: 0,
      },
    };
  }

  /** Vues et spectateurs distincts, tous contenus confondus. */
  async getViewStatsSummary(): Promise<{ totalViews: number; uniqueViewers: number }> {
    const response = await this.api.get("/admin/content/view-stats/summary");
    return response.data;
  }

  /** Vues par contenu, pour la liste des contenus. */
  async getViewStats(): Promise<
    Array<{ contentId: number; views: number; uniqueViewers: number }>
  > {
    const response = await this.api.get("/admin/content/view-stats");
    return response.data;
  }

  async getContentStats(): Promise<ContentStats> {
    const stats = await this.getDashboardStats();

    return {
      totalContents: stats.totalContents,
      publishedContents: stats.publishedContents,
      draftContents: stats.draftContents || 0,
      pendingContents: stats.pendingContents || 0,
      topGenres: [],
      topContents: [],
      averageRating: 0,
      totalViews: 0
    };
  }

  // === CONTENT MANAGEMENT ===
  async getContents(
    filters?: ContentFilters,
    page = 1,
    pageSize = 20
  ): Promise<PaginatedResponse<Content>> {
    const params = new URLSearchParams();
    if (filters?.type) params.append("typeId", filters.type.toString());
    if (filters?.genre) params.append("genreId", filters.genre.toString());
    if (filters?.status) params.append("status", filters.status);
    if (filters?.search) params.append("title", filters.search);
    if (filters?.dateFrom) params.append("dateFrom", filters.dateFrom);
    if (filters?.dateTo) params.append("dateTo", filters.dateTo);
    if (filters?.tags?.length) params.append("tags", filters.tags.join(","));

    // Le backend utilise page basé sur 0, pas 1
    params.append("page", (page - 1).toString());
    params.append("pageSize", pageSize.toString());

    const response = await this.api.get(`/admin/content?${params.toString()}`);

    console.log("API getContents - URL:", `/admin/content?${params.toString()}`);
    console.log("API getContents - Response:", response);
    console.log("API getContents - Response data:", response.data);

    // Le backend retourne un objet paginé avec data, total, page, pageSize
    const data = response.data?.data || response.data || [];

    // Mapper les champs snake_case vers camelCase
    const mappedData = data.map((item: any) => ({
      ...item,
      posterUrl: item.poster_path || item.posterUrl,
      releaseDate: item.release_date || item.releaseDate,
    }));

    const total = response.data?.total || data.length;

    return {
      data: mappedData,
      total: total,
      page: page,
      pageSize: pageSize,
      totalPages: Math.ceil(total / pageSize),
    };
  }

  async getContent(id: number): Promise<Content> {
    const response = await this.api.get(`/admin/content/${id}`);
    return response.data;
  }

  async createContent(content: Partial<Content>): Promise<Content> {
    const response = await this.api.post("/admin/content", content);
    return response.data;
  }

  async updateContent(id: number, content: Partial<Content>): Promise<Content> {
    const response = await this.api.put(`/admin/content/${id}`, content);
    return response.data;
  }

  async deleteContent(id: number): Promise<void> {
    await this.api.delete(`/admin/content/${id}`);
  }

  // === GENRES & TYPES ===
  async getGenres(): Promise<any[]> {
    const response = await this.api.get("/admin/content/genres");
    return response.data;
  }

  /**
   * Langues d'origine deja employees par des contenus.
   *
   * Complete la liste ecrite en dur du formulaire : une langue ajoutee une fois
   * reste ainsi disponible, sans table a maintenir.
   */
  async getUsedLanguages(): Promise<string[]> {
    const response = await this.api.get("/admin/content/languages");
    return Array.isArray(response.data) ? response.data : [];
  }

  async getContentTypes(): Promise<any[]> {
    const response = await this.api.get("/admin/content/types");
    return response.data;
  }

  async createGenre(genre: any): Promise<any> {
    const response = await this.api.post("/admin/content/genres", genre);
    return response.data;
  }

  async updateGenre(id: number, genre: any): Promise<any> {
    const response = await this.api.put(`/admin/content/genres/${id}`, genre);
    return response.data;
  }

  async deleteGenre(id: number): Promise<void> {
    await this.api.delete(`/admin/content/genres/${id}`);
  }

  // === TYPES CRUD ===
  async createType(type: any): Promise<any> {
    const response = await this.api.post("/types", type);
    return response.data;
  }

  async updateType(id: number, type: any): Promise<any> {
    const response = await this.api.put(`/types/${id}`, type);
    return response.data;
  }

  async deleteType(id: number): Promise<void> {
    await this.api.delete(`/types/${id}`);
  }

  // === ANALYTICS ===
  async getAnalyticsDashboard(days = 7): Promise<any> {
    const response = await this.api.get(`/admin/analytics/dashboard?days=${days}`);
    return response.data;
  }

  async getMostWatched(days = 30, limit = 10): Promise<any> {
    const response = await this.api.get(`/admin/analytics/most-watched?days=${days}&limit=${limit}`);
    return response.data;
  }

  async getContentStatistics(contentId: number, days = 30): Promise<any> {
    const response = await this.api.get(`/admin/analytics/content/${contentId}/statistics?days=${days}`);
    return response.data;
  }

  async getBandwidthByLocation(days = 30): Promise<any> {
    const response = await this.api.get(`/admin/analytics/bandwidth-by-location?days=${days}`);
    return response.data;
  }

  // === ACTORS & CASTING ===
  async getActors(
    page = 1,
    pageSize = 20,
    search?: string
  ): Promise<PaginatedResponse<Actor>> {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    params.append("page", page.toString());
    params.append("pageSize", pageSize.toString());

    const response = await this.api.get(`/actors?${params.toString()}`);
    return response.data;
  }

  async getActor(id: number): Promise<Actor> {
    const response = await this.api.get(`/actors/${id}`);
    return response.data;
  }

  async createActor(actor: Partial<Actor>): Promise<Actor> {
    const response = await this.api.post("/actors", actor);
    return response.data;
  }

  async updateActor(id: number, actor: Partial<Actor>): Promise<Actor> {
    const response = await this.api.put(`/actors/${id}`, actor);
    return response.data;
  }

  async deleteActor(id: number): Promise<void> {
    await this.api.delete(`/actors/${id}`);
  }

  // === CASTING ===
  async getCasts(contentId: number): Promise<Cast[]> {
    const response = await this.api.get(`/admin/content/${contentId}/casts`);
    return response.data;
  }

  async createCast(cast: Partial<Cast>): Promise<Cast> {
    const response = await this.api.post("/casts", cast);
    return response.data;
  }

  async updateCast(id: number, cast: Partial<Cast>): Promise<Cast> {
    const response = await this.api.put(`/casts/${id}`, cast);
    return response.data;
  }

  async deleteCast(id: number): Promise<void> {
    await this.api.delete(`/casts/${id}`);
  }

  // === USER MANAGEMENT === (via Keycloak through BFF)

  /**
   * Statistiques reelles sur les comptes. La page « Gestion des utilisateurs »
   * affichait des nombres inventes (24 521 comptes, « +12 % vs mois dernier »).
   * `growthPercent` vaut null quand il n'y a pas de mois precedent auquel se
   * comparer : dans ce cas la page n'annonce aucune evolution.
   */
  /**
   * Roles reels du realm.
   *
   * Le filtre par role proposait « USER » et « MODERATOR », qui n'existent pas.
   */
  async getRealmRoles(): Promise<Array<{ name: string; description?: string }>> {
    const response = await this.api.get("/admin/users/roles");
    return response.data;
  }

  async getUserStats(): Promise<{
    total: number;
    enabled: number;
    disabled: number;
    newLast30Days: number;
    newPrevious30Days: number;
    growthPercent: number | null;
    partial?: boolean;
  }> {
    const response = await this.api.get("/admin/users/stats");
    return response.data;
  }

  /**
   * Comptes, restreints a une population.
   *
   * @param population "APPLICATION" pour les abonnes, "BACKOFFICE" pour
   *                   l'equipe, omis pour tous les comptes
   */
  async getUsers(
    page = 1,
    pageSize = 20,
    search?: string,
    population?: string,
    role?: string,
    status?: string
  ): Promise<PaginatedResponse<UserManagement>> {
    const params = new URLSearchParams();
    // Backend expects 0-based page index
    params.append("page", Math.max(0, page - 1).toString());
    params.append("size", pageSize.toString());
    if (search) params.append("search", search);
    if (population) params.append("population", population);
    if (role) params.append("role", role);
    if (status) params.append("status", status);

    const response = await this.api.get(`/admin/users?${params.toString()}`);
    return response.data;
  }

  async getUser(id: string): Promise<UserManagement> {
    const response = await this.api.get(`/admin/users/${id}`);
    return response.data;
  }

  async createUser(user: {
    username: string;
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    roles?: string[];
    enabled?: boolean;
    emailVerified?: boolean;
  }): Promise<UserManagement> {
    const response = await this.api.post("/admin/users", user);
    return response.data;
  }

  async updateUser(
    id: string,
    user: {
      username?: string;
      email?: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      roles?: string[];
      enabled?: boolean;
    }
  ): Promise<UserManagement> {
    const response = await this.api.put(`/admin/users/${id}`, user);
    return response.data;
  }

  async deleteUser(id: string): Promise<void> {
    await this.api.delete(`/admin/users/${id}`);
  }

  async setUserEnabled(id: string, enabled: boolean): Promise<UserManagement> {
    const response = await this.api.patch(`/admin/users/${id}/status`, { enabled });
    return response.data;
  }

  // === NOTIFICATIONS ===
  /**
   * Diffusions recentes, regroupees par annonce.
   *
   * L'ancien appel visait `/notifications`, qu'aucun controleur ne servait :
   * tous ces appels tombaient en 404. La table des notifications portant une
   * ligne par destinataire, le serveur regroupe desormais par annonce.
   */
  async getBroadcasts(
    page = 1,
    pageSize = 20
  ): Promise<{
    data: Array<{
      type: string;
      title: string;
      message: string;
      recipients: number;
      readCount: number;
      sentAt: string | null;
    }>;
    total: number;
  }> {
    const params = new URLSearchParams();
    params.append("page", Math.max(0, page - 1).toString());
    params.append("pageSize", pageSize.toString());
    const response = await this.api.get(
      `/admin/notifications?${params.toString()}`
    );
    return response.data;
  }

  /** Types de notification acceptes par le serveur. */
  async getNotificationTypes(): Promise<string[]> {
    const response = await this.api.get("/admin/notifications/types");
    return response.data;
  }

  /** Diffuse une notification aux abonnes ayant active la preference. */
  async broadcastNotification(payload: {
    type: string;
    title: string;
    message: string;
    contentId?: number | null;
    imageUrl?: string | null;
  }): Promise<{ recipients: number; type: string }> {
    const response = await this.api.post(
      "/admin/notifications/broadcast",
      payload
    );
    return response.data;
  }

  async createNotification(
    notification: Partial<Notification>
  ): Promise<Notification> {
    const response = await this.api.post("/notifications", notification);
    return response.data;
  }

  async updateNotification(
    id: number,
    notification: Partial<Notification>
  ): Promise<Notification> {
    const response = await this.api.put(`/notifications/${id}`, notification);
    return response.data;
  }

  async deleteNotification(id: number): Promise<void> {
    await this.api.delete(`/notifications/${id}`);
  }

  async getNotificationTemplates(): Promise<NotificationTemplate[]> {
    const response = await this.api.get("/notifications/templates");
    return response.data;
  }

  /**
   * Sante de chaque service, et faits d'infrastructure.
   *
   * La page interrogeait `/actuator/health`, qui n'aboutit qu'au backoffice-bff
   * — celui qui repond toujours. Le serveur sonde desormais chaque service.
   */
  async getSystemHealth(): Promise<{
    services: Array<{
      name: string;
      status: string;
      responseMs?: number;
      detail?: string;
    }>;
    total: number;
    down: number;
    status: string;
    infrastructure: Record<string, string>;
  }> {
    const response = await this.api.get("/admin/system/health");
    return response.data;
  }

  // Limites par compte abonné (UX-01) : appareils connectés (Keycloak),
  // profils adultes et enfants (client-bff).
  async getAccountLimits(): Promise<AccountLimitsResponse> {
    const response = await this.api.get("/admin/account-limits");
    return response.data;
  }

  async updateAccountLimits(limits: {
    maxDevices: number;
    maxAdultProfiles: number;
    maxKidProfiles: number;
  }): Promise<AccountLimitsResponse> {
    const response = await this.api.put("/admin/account-limits", limits);
    return response.data;
  }

  async getNotificationStats(): Promise<{
    total: number;
    read: number;
    unread: number;
    readRate: number | null;
    recipients: number;
  }> {
    const response = await this.api.get("/admin/notifications/stats");
    return response.data;
  }

  // === PROCESSING STATUS (temps réel) ===
  async getProcessingStatus(contentId: number): Promise<ProcessingStatus> {
    const response = await this.api.get(`/admin/content/${contentId}/processing-status`);
    return response.data;
  }

  // === MEDIA PROCESSING ===
  async uploadMedia(
    file: File,
    contentType: string,
    contentId?: number,
    onProgress?: (progress: number) => void
  ): Promise<{ uploadId: string; uploadUrl: string }> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("contentType", contentType);
    if (contentId) formData.append("contentId", contentId.toString());

    // Utiliser l'endpoint approprié selon le type de contenu
    let endpoint = "/admin/content/upload";

    // Si c'est un épisode, utiliser l'endpoint spécifique
    if (contentType === "EPISODE" && contentId) {
      endpoint = `/episodes/${contentId}/upload`;
    }

    const response = await this.api.post(endpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });
    return response.data;
  }

  async getMediaProcessingStatus(
    uploadId: string
  ): Promise<MediaProcessingStatus> {
    const response = await this.api.get(`/processing/status/${uploadId}`);
    return response.data;
  }

  async processVideo(
    contentId: number,
    videoFile: File,
    quality: string = "1080p"
  ): Promise<{ uploadId: string; message: string }> {
    const formData = new FormData();
    formData.append("video", videoFile);
    formData.append("contentId", contentId.toString());
    formData.append("quality", quality);

    // Utiliser l'endpoint du content-service pour le traitement vidéo
    const response = await this.api.post("/admin/content/process", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }

  // === TRAILER UPLOAD ===
  // === SOUS-TITRES ===
  // Fichiers .srt ou .vtt, independants de la video : ajouter une langue ne
  // declenche aucun retranscodage.

  async uploadSubtitle(
    contentId: number,
    file: File,
    language: string,
    label?: string
  ): Promise<{ language: string; label: string; folder: string; url: string }> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("language", language);
    if (label) {
      formData.append("label", label);
    }

    const response = await this.api.post(`/admin/content/${contentId}/subtitles`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  async listSubtitles(
    contentId: number
  ): Promise<Array<{ language: string; label: string; folder: string; url: string }>> {
    const response = await this.api.get(`/admin/content/${contentId}/subtitles`);
    return response.data;
  }

  async deleteSubtitle(contentId: number, language: string): Promise<void> {
    await this.api.delete(`/admin/content/${contentId}/subtitles/${language}`);
  }

  async uploadTrailer(
    contentId: number,
    trailerFile: File,
    onProgress?: (progress: number) => void
  ): Promise<{ uploadId: string; uploadUrl: string }> {
    const formData = new FormData();
    formData.append("file", trailerFile);
    formData.append("contentType", "TRAILER");
    formData.append("contentId", contentId.toString());

    const response = await this.api.post(`/admin/content/${contentId}/trailer/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 0, // Pas de timeout pour les gros fichiers
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });
    return response.data;
  }

  /**
   * Televersement direct du navigateur vers le stockage (CDN-05).
   *
   * Le fichier ne traverse plus nos services : le serveur signe une URL par
   * morceau, le navigateur les envoie a R2, puis le serveur assemble. C'est
   * l'assemblage — confirme par le stockage — qui declenche le transcodage.
   *
   * Au passage, un morceau qui echoue est reessaye seul : une coupure ne fait
   * plus tout recommencer.
   */
  async uploadVideo(
    contentId: number,
    videoFile: File,
    onProgress?: (progress: number) => void,
    videoType: "VIDEO" | "TRAILER" = "VIDEO"
  ): Promise<{ objectPath: string; size: number }> {
    const ouverture = await this.api.post(
      `/admin/content/${contentId}/upload-direct/ouvrir`,
      {
        videoType,
        fileName: videoFile.name,
        fileSize: videoFile.size,
      }
    );

    const { uploadId, objectPath, partSize, partUrls, uploadedParts } =
      ouverture.data as {
        uploadId: string;
        objectPath: string;
        partSize: number;
        partUrls: string[];
        uploadedParts?: Array<{ partNumber: number; etag: string; size: number }>;
      };

    // Morceaux que le stockage detient deja : un envoi interrompu pres de la
    // fin reprend la ou il s'etait arrete au lieu de renvoyer plusieurs
    // gigaoctets.
    const dejaPresents = new Map<number, { etag: string; size: number }>();
    (uploadedParts ?? []).forEach((m) =>
      dejaPresents.set(m.partNumber, { etag: m.etag, size: m.size })
    );

    const parts: Array<{ partNumber: number; etag: string }> = [];
    let envoye = 0;
    dejaPresents.forEach((m, numero) => {
      parts.push({ partNumber: numero, etag: m.etag });
      envoye += m.size;
    });
    if (dejaPresents.size > 0) {
      console.log(
        `Reprise du televersement : ${dejaPresents.size} morceaux sur ${partUrls.length} deja presents`
      );
      onProgress?.(
        Math.min(99, Math.round((envoye * 100) / videoFile.size))
      );
    }

    let prochain = 0;
    let interrompu = false;

    // La session Keycloak expire apres trente minutes sans appel authentifie.
    // Les morceaux partent vers Cloudflare avec des URL signees, donc sans
    // authentification : sans ce battement, l'assemblage final arrivait sur
    // une session morte et les heures de transfert etaient perdues.
    const battement = window.setInterval(() => {
      void getAccessToken();
    }, 4 * 60 * 1000);

    // Les morceaux partaient un par un. Sur un televersement de plusieurs
    // gigaoctets vers Cloudflare, le temps d'aller-retour de chaque requete
    // s'ajoutait a la precedente : le transfert etait bien plus lent qu'en
    // passant par notre serveur, qui ouvrait une seule connexion continue.
    // Quatre envois simultanes remettent le debit de la ligne au premier plan.
    const CONCURRENCE = 4;

    const travailleur = async (): Promise<void> => {
      while (!interrompu) {
        const index = prochain++;
        if (index >= partUrls.length) return;
        if (dejaPresents.has(index + 1)) {
          continue; // deja chez le stockage
        }

        const debut = index * partSize;
        const morceau = videoFile.slice(
          debut,
          Math.min(debut + partSize, videoFile.size)
        );

        try {
          const etag = await this.envoyerMorceau(
            partUrls[index],
            morceau,
            index + 1
          );
          parts.push({ partNumber: index + 1, etag });
        } catch (erreur) {
          // Inutile de continuer a envoyer : l'assemblage echouerait de toute
          // facon sans ce morceau.
          interrompu = true;
          throw erreur;
        }

        envoye += morceau.size;
        // On garde 99 % pour l'assemblage : annoncer 100 % avant qu'il
        // reussisse serait mensonger — c'est exactement ce qui rendait
        // l'ancien echec si deroutant.
        onProgress?.(Math.min(99, Math.round((envoye * 100) / videoFile.size)));
      }
    };

    try {
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCE, partUrls.length) }, travailleur)
      );

      // Les morceaux n'arrivent pas dans l'ordre : l'assemblage l'exige.
      parts.sort((a, b) => a.partNumber - b.partNumber);

      const fin = await this.terminerTeleversement(contentId, {
        videoType,
        uploadId,
        objectPath,
        parts,
      });
      onProgress?.(100);
      return fin;
    } catch (erreur) {
      // On n'abandonne plus le televersement : les morceaux deja recus
      // permettent de reprendre, et le rattrapage cote serveur nettoie ce qui
      // ne sera jamais termine. Abandonner ici faisait perdre des heures de
      // transfert au premier incident reseau.
      throw erreur;
    } finally {
      window.clearInterval(battement);
    }
  }

  /**
   * Demande l'assemblage.
   *
   * Sans delai d'expiration : assembler plus de deux cents morceaux prend
   * parfois plus que les 30 s du reglage general, et l'appel echouait alors
   * alors que le fichier etait entierement arrive — le televersement semblait
   * perdu. Cinq tentatives, car cet appel conclut des heures de transfert.
   *
   * Si malgre tout il n'aboutit pas, le serveur rattrape le televersement de
   * lui-meme : il sait ce qu'il a ouvert et interroge le stockage.
   */
  private async terminerTeleversement(
    contentId: number,
    corps: Record<string, unknown>
  ): Promise<{ objectPath: string; size: number }> {
    let derniereErreur: unknown;

    // Cet appel conclut des heures de transfert : il vaut la peine d'insister.
    const TENTATIVES_ASSEMBLAGE = 5;

    for (let essai = 1; essai <= TENTATIVES_ASSEMBLAGE; essai++) {
      try {
        const reponse = await this.api.post(
          `/admin/content/${contentId}/upload-direct/terminer`,
          corps,
          { timeout: 0 }
        );
        return reponse.data;
      } catch (e) {
        derniereErreur = e;
        if (essai < TENTATIVES_ASSEMBLAGE) {
          await new Promise((r) => setTimeout(r, 2000 * essai));
        }
      }
    }
    throw derniereErreur;
  }

  /**
   * Envoie un morceau et renvoie son ETag, que le serveur utilisera pour
   * l'assemblage.
   *
   * Cinq tentatives avec attente doublee : sur un televersement de plusieurs
   * heures, un echec ponctuel est normal et ne doit pas tout perdre. Un rejet
   * de fetch ne dit pas si la cause est le reseau ou une reponse d'erreur du
   * stockage sans en-tete CORS — dans les deux cas, reessayer est la reponse.
   */
  private async envoyerMorceau(
    url: string,
    morceau: Blob,
    numero: number
  ): Promise<string> {
    let derniereErreur: unknown;

    const TENTATIVES_MORCEAU = 5;

    for (let essai = 1; essai <= TENTATIVES_MORCEAU; essai++) {
      try {
        const reponse = await fetch(url, { method: "PUT", body: morceau });
        if (!reponse.ok) {
          throw new Error(
            `morceau ${numero} refuse (HTTP ${reponse.status})`
          );
        }
        const etag = reponse.headers.get("ETag");
        if (!etag) {
          // Sans en-tete ETag expose par CORS, l'assemblage est impossible.
          throw new Error(
            "ETag absent de la reponse : verifier la configuration CORS du bucket R2"
          );
        }
        return etag;
      } catch (e) {
        derniereErreur = e;
        if (essai < TENTATIVES_MORCEAU) {
          await new Promise((r) => setTimeout(r, 2000 * 2 ** (essai - 1)));
        }
      }
    }
    // Sans le numero, un echec apparaissait comme un « Failed to fetch » nu :
    // impossible de savoir quel morceau reprendre.
    throw new Error(
      `morceau ${numero} en echec apres ${TENTATIVES_MORCEAU} tentatives : ` +
        `${derniereErreur instanceof Error ? derniereErreur.message : derniereErreur}`
    );
  }

  /**
   * Upload de l'affiche (poster) d'un contenu
   */
  async uploadPoster(
    contentId: number,
    posterFile: File,
    onProgress?: (progress: number) => void
  ): Promise<{ objectPath: string; presignedUrl: string }> {
    const formData = new FormData();
    formData.append("file", posterFile);

    const response = await this.api.post(
      `/admin/content/${contentId}/poster/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      }
    );
    return response.data;
  }

  /**
   * Upload du backdrop (image de fond) d'un contenu
   */
  async uploadBackdrop(
    contentId: number,
    backdropFile: File,
    onProgress?: (progress: number) => void
  ): Promise<{ objectPath: string; presignedUrl: string }> {
    const formData = new FormData();
    formData.append("file", backdropFile);

    const response = await this.api.post(
      `/admin/content/${contentId}/backdrop/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      }
    );
    return response.data;
  }

  // === SEASONS & EPISODES MANAGEMENT ===
  async getSeasons(contentId: number): Promise<Season[]> {
    const response = await this.api.get(`/v1/contents/${contentId}/seasons`);
    return response.data;
  }

  async createSeason(contentId: number, seasonData: Partial<Season>): Promise<Season> {
    const response = await this.api.post(`/v1/contents/${contentId}/seasons`, seasonData);
    return response.data;
  }

  async updateSeason(seasonId: number, seasonData: Partial<Season>): Promise<Season> {
    const response = await this.api.put(`/seasons/${seasonId}`, seasonData);
    return response.data;
  }

  async deleteSeason(seasonId: number): Promise<void> {
    await this.api.delete(`/seasons/${seasonId}`);
  }

  async getEpisodes(seasonId: number): Promise<Episode[]> {
    const response = await this.api.get(`/seasons/${seasonId}/episodes`);
    return response.data;
  }

  async createEpisode(seasonId: number, episodeData: Partial<Episode>): Promise<Episode> {
    const response = await this.api.post(`/seasons/${seasonId}/episodes`, episodeData);
    return response.data;
  }

  async updateEpisode(episodeId: number, episodeData: Partial<Episode>): Promise<Episode> {
    const response = await this.api.put(`/episodes/${episodeId}`, episodeData);
    return response.data;
  }

  async deleteEpisode(episodeId: number): Promise<void> {
    await this.api.delete(`/episodes/${episodeId}`);
  }

  // === SUBSCRIPTIONS & PAYMENTS (Admin) ===
  async getPaymentTransactions(
    filters?: { status?: string; provider?: string; country?: string; startDate?: string; endDate?: string; userId?: string },
    page = 1,
    size = 20
  ): Promise<any> {
    const params = new URLSearchParams();
    params.append("page", (page - 1).toString());
    params.append("size", size.toString());
    if (filters?.status) params.append("status", filters.status);
    if (filters?.provider) params.append("provider", filters.provider);
    if (filters?.country) params.append("country", filters.country);
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);
    if (filters?.userId) params.append("userId", filters.userId);
    const response = await this.api.get(`/admin/payments/transactions?${params.toString()}`);
    return response.data;
  }

  async getPaymentTransactionDetails(id: number): Promise<any> {
    const response = await this.api.get(`/admin/payments/transactions/${id}`);
    return response.data;
  }

  async refundPayment(id: number): Promise<any> {
    const response = await this.api.post(`/admin/payments/transactions/${id}/refund`);
    return response.data;
  }

  async getPaymentProviders(): Promise<any[]> {
    const response = await this.api.get("/admin/payments/providers");
    return response.data;
  }

  async updatePaymentProvider(id: number, data: any): Promise<any> {
    const response = await this.api.put(`/admin/payments/providers/${id}`, data);
    return response.data;
  }

  async togglePaymentProvider(id: number): Promise<any> {
    const response = await this.api.post(`/admin/payments/providers/${id}/toggle`);
    return response.data;
  }

  async createPaymentProvider(data: Partial<import("@/types").PaymentProviderConfig>): Promise<any> {
    const response = await this.api.post("/admin/payments/providers", data);
    return response.data;
  }

  async deletePaymentProvider(id: number): Promise<any> {
    const response = await this.api.delete(`/admin/payments/providers/${id}`);
    return response.data;
  }

  async getPaymentCountries(): Promise<any> {
    const response = await this.api.get("/admin/payments/countries");
    return response.data;
  }

  async getPaymentStatistics(startDate?: string, endDate?: string, country?: string): Promise<any> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (country) params.append("country", country);
    const query = params.toString();
    const response = await this.api.get(`/admin/payments/statistics${query ? `?${query}` : ""}`);
    return response.data;
  }

  async getAdminSubscriptions(page = 1, size = 20, status?: string): Promise<any> {
    const params = new URLSearchParams();
    params.append("page", (page - 1).toString());
    params.append("size", size.toString());
    if (status) params.append("status", status);
    const response = await this.api.get(`/admin/payments/subscriptions?${params.toString()}`);
    return response.data;
  }

  async cancelSubscription(id: number): Promise<any> {
    const response = await this.api.put(`/admin/payments/subscriptions/${id}/cancel`);
    return response.data;
  }

  async grantSubscription(data: { userId: string; planId: string; durationDays?: number; note?: string }): Promise<any> {
    const response = await this.api.post(`/admin/payments/subscriptions/grant`, data);
    return response.data;
  }

  // === PLANS ===
  async getPlans(): Promise<any[]> {
    const response = await this.api.get("/plans");
    return response.data;
  }

  async createPlan(plan: any): Promise<any> {
    const response = await this.api.post("/plans", plan);
    return response.data;
  }

  async updatePlan(id: number, plan: any): Promise<any> {
    const response = await this.api.put(`/plans/${id}`, plan);
    return response.data;
  }

  async deletePlan(id: number): Promise<void> {
    await this.api.delete(`/plans/${id}`);
  }

  // === PAYMENT METHODS ===
  async getPaymentMethods(): Promise<any[]> {
    const response = await this.api.get("/payment-methods");
    return response.data;
  }

  async createPaymentMethod(method: any): Promise<any> {
    const response = await this.api.post("/payment-methods", method);
    return response.data;
  }

  async updatePaymentMethod(id: number, method: any): Promise<any> {
    const response = await this.api.put(`/payment-methods/${id}`, method);
    return response.data;
  }

  async deletePaymentMethod(id: number): Promise<void> {
    await this.api.delete(`/payment-methods/${id}`);
  }
}

export const apiService = new ApiService();
export default apiService;
